<script setup lang="ts">
import { computed, inject, onMounted, ref, useTemplateRef, watch } from "vue";

import { type TreeAccordionNode } from "@/components/ui/TreeAccordion.vue";

const {
  name,
  label,
  children,
  isRoot,
  actions,
  enabled,
  icon,
  totalCount,
  foldable = true,
  selectedPath,
  path,
} = defineProps<TreeAccordionNode>();
const emitItemAction = inject<(action: string, itemName: string) => void>("emitItemAction");
const emitItemSelected = inject<(itemPath: string[]) => void>("emitItemSelected");

const isCollapsed = ref(false);
const childrenHeight = ref("Opx");
const childrenContainer = useTemplateRef<HTMLDivElement>("childrenContainer");

const hasChildren = computed(() => children?.length);
const displayedLabel = computed(() => {
  if (label) {
    return label;
  } else {
    const segment = name.split("/");
    return segment[segment.length - 1];
  }
});
const totalChildrenCount = computed(() => {
  return countChildrenRecursively(children);
});
const isSelected = computed(() => {
  if (selectedPath) {
    return selectedPath.length === 1 && selectedPath[0] === name;
  }
  return false;
});
const hasTotalCount = computed(() => {
  return totalCount !== undefined && totalCount > 1;
});

function toggleChildren() {
  if (foldable && hasChildren.value) {
    isCollapsed.value = !isCollapsed.value;
  }
}

function countChildrenRecursively(children?: TreeAccordionNode[]): number {
  if (!children || children.length === 0) {
    return 0;
  }

  return children.reduce((total, child) => {
    return total + 1 + countChildrenRecursively(child.children);
  }, 0);
}

function onItemSelected() {
  if (emitItemSelected) {
    emitItemSelected(path ?? []);
  }
}

function onAction(action: string) {
  if (emitItemAction) {
    emitItemAction(action, name);
  }
}

function measureChildrenHeight() {
  const h = childrenContainer.value?.clientHeight ?? 0;
  childrenHeight.value = `${h}px`;
}

watch(
  () => children,
  () => {
    measureChildrenHeight();
  }
);

onMounted(() => {
  measureChildrenHeight();
});
</script>

<template>
  <div
    class="tree-accordion-item"
    :class="{ first: isRoot, enabled }"
    :style="{
      '--children-count': totalChildrenCount,
    }"
    :data-name="name"
  >
    <div class="wrapper" @click="toggleChildren">
      <div
        class="label"
        :class="{
          'has-total-count': hasTotalCount,
          'is-selected': isSelected,
        }"
        @click="onItemSelected"
      >
        <div class="inner-label" :class="{ 'has-border': hasChildren && hasTotalCount && !isRoot }">
          <span v-if="icon !== undefined" class="icon" :class="icon" />
          <span class="text">{{ displayedLabel }}</span>
          <span v-if="hasTotalCount" class="total-count">{{ totalCount }}</span>
        </div>
      </div>
      <div class="actions" v-if="enabled">
        <button
          v-for="(action, index) in actions"
          class="paragraph-s"
          :key="index"
          @click="action.enabled && onAction(action.actionName)"
          :disabled="!action.enabled"
        >
          <span :class="action.icon" />
        </button>
      </div>
      <button
        v-if="hasChildren && foldable"
        class="collapse paragraph-s"
        :class="{ collapsed: isCollapsed }"
        aria-label="Collapse"
      >
        <span class="icon icon-chevron-down" />
      </button>
    </div>
    <Transition name="toggle-children">
      <div
        v-if="hasChildren && !isCollapsed"
        class="children"
        ref="childrenContainer"
        :style="{
          '--children-height': childrenHeight,
        }"
      >
        <TreeAccordionItem
          v-for="(child, index) in children"
          :key="index"
          :name="child.name"
          :children="child.children"
          :is-root="false"
          :actions="child.actions"
          :enabled="child.enabled"
          :icon="child.icon"
          :total-count="child.totalCount"
          :foldable="child.foldable"
          :selected-path="selectedPath?.filter((p) => p !== name)"
          :path="[...(path ?? []), child.name]"
        />
      </div>
    </Transition>
  </div>
</template>

<style scoped>
.tree-accordion-item {
  position: relative;
  overflow: hidden;
  margin-left: 19px;

  &.first {
    margin-left: 0;
  }

  .wrapper {
    position: relative;
    z-index: 2;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: space-between;
    border-radius: var(--radius-xs);
    transition: background-color var(--animation-duration) var(--animation-easing);

    .label {
      display: flex;
      height: var(--label-height);
      flex-direction: row;
      align-items: center;
      padding: var(--spacing-4) var(--spacing-8);
      border-radius: var(--radius-xs);
      border-left-width: var(--stroke-width-m);
      border-left-style: solid;
      border-image: linear-gradient(
          to bottom,
          transparent 20%,
          var(--color-stroke-background-primary) 21%,
          var(--color-stroke-background-primary),
          var(--color-stroke-background-primary) 79%,
          transparent 80%
        )
        1;
      cursor: pointer;
      overflow-x: hidden;
      white-space: nowrap;

      .inner-label {
        display: flex;
        flex: 1;
        flex-direction: row;
        align-items: center;
        border-radius: var(--radius-xs);
      }

      &.has-total-count {
        padding: var(--spacing-4) var(--spacing-6);

        .inner-label {
          padding: var(--spacing-4);
          background-color: var(--color-background-primary);

          &.has-border {
            border: solid var(--stroke-width-m) var(--color-stroke-background-primary);
          }
        }

        .total-count {
          padding: var(--spacing-6);
          border-radius: var(--radius-xs);
          margin-left: var(--spacing-4);
          background-color: var(--color-background-secondary);
          font-size: 0.7em;
          line-height: 0.7em;
        }
      }

      .icon {
        margin-right: var(--spacing-4);
        color: var(--color-icon-secondary);
      }

      .text {
        overflow: hidden;
        border-radius: var(--radius-xs);
        color: var(--color-text-primary);
        text-overflow: ellipsis;
      }
    }

    .actions {
      display: flex;
      flex-direction: row;
      align-items: center;
      gap: var(--spacing-8);
      opacity: 0;
      transition: opacity var(--animation-duration) var(--animation-easing);

      button {
        padding: 0;
        border: none;
        margin: 0;
        background-color: transparent;
        color: var(--color-text-secondary);
        cursor: pointer;
        transition: color var(--animation-duration) var(--animation-easing);

        &:hover {
          color: var(--color-text-primary);
        }

        &:disabled {
          cursor: not-allowed;
          opacity: 0.7;
        }
      }
    }

    .collapse {
      padding: 0;
      border: none;
      margin: 0;
      background-color: transparent;
      color: var(--color-text-secondary);
      cursor: pointer;
      transition: transform var(--animation-duration) var(--animation-easing);

      &.collapsed {
        transform: rotate(90deg);
      }
    }

    &:hover {
      .actions {
        opacity: 1;
      }
    }
  }

  &.enabled > .wrapper {
    cursor: pointer;

    &:hover {
      background-color: var(--color-background-secondary);

      .label.has-total-count {
        background-color: var(--color-background-secondary);
      }
    }
  }

  &.enabled > .wrapper:has(> .label.is-selected) {
    background-color: var(--color-background-secondary);

    .label {
      border-image: linear-gradient(to bottom, transparent, transparent) 1;
    }
  }

  .children {
    position: relative;
    z-index: 1;
    display: flex;
    flex-direction: column;
    padding-top: var(--spacing-6);
    gap: var(--spacing-4);
  }

  .toggle-children-move,
  .toggle-children-enter-active,
  .toggle-children-leave-active {
    transform-origin: top;
    transition: all var(--animation-duration) var(--animation-easing);
  }

  .toggle-children-enter-from,
  .toggle-children-leave-to {
    margin-top: calc(var(--children-height) * -1);
    opacity: 0;
  }
}
</style>
